import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import translate from "@insite/client-framework/Translate";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { MainNavigationStyles, MappedLink } from "@insite/content-library/Widgets/Header/MainNavigation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import Popover from "@insite/mobius/Popover";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement | null>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
}

type Props = ThemeProps<BaseTheme> & OwnProps;

function filterMegaMenuLevel1Child(child: MappedLink, displayChangeCustomerLink: boolean): boolean {
    if (child.excludeFromNavigation) {
        return false;
    }
    if (child.type === "ChangeCustomerPage" && !displayChangeCustomerLink) {
        return false;
    }
    if (child.type === "VmiUsersPage") {
        const currentMode = getCookie(NavigationModeCookieName);
        if (!currentMode || currentMode === "Storefront") {
            return false;
        }
    }
    return true;
}

function filterMegaMenuLevel2Child(grandChild: MappedLink, displayChangeCustomerLink: boolean): boolean {
    if (grandChild.excludeFromNavigation) {
        return false;
    }
    if (grandChild.type === "ChangeCustomerPage" && !displayChangeCustomerLink) {
        return false;
    }
    if (grandChild.type === "VmiUsersPage") {
        const currentMode = getCookie(NavigationModeCookieName);
        if (!currentMode || currentMode === "Storefront") {
            return false;
        }
    }
    return true;
}

function hasSubcategoryMenu(child: MappedLink, displayChangeCustomerLink: boolean): boolean {
    if (!child.children || child.children.length === 0) {
        return false;
    }
    return child.children.some(c => filterMegaMenuLevel2Child(c, displayChangeCustomerLink));
}

function initialLevel1Index(children: MappedLink[], displayChangeCustomerLink: boolean): number {
    const withSubs = children.findIndex(c => hasSubcategoryMenu(c, displayChangeCustomerLink));
    return withSubs >= 0 ? withSubs : 0;
}

interface State {
    selectedLevel1Index: number;
}

class MainNavigationItem extends React.Component<Props, State> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    state: State = {
        selectedLevel1Index: 0,
    };

    componentDidMount() {
        window.addEventListener("scroll", this.closePopoverOnScroll, { passive: true });
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.closePopoverOnScroll);
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
            const level1 = this.getLevel1Children();
            this.setState({
                selectedLevel1Index: Math.min(initialLevel1Index(level1, this.props.displayChangeCustomerLink), Math.max(0, level1.length - 1)),
            });
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover();
        }

        if (
            this.props.link.title !== prevProps.link.title ||
            this.props.link.children?.length !== prevProps.link.children?.length ||
            this.props.displayChangeCustomerLink !== prevProps.displayChangeCustomerLink
        ) {
            const level1 = this.getLevel1Children();
            const next = Math.min(initialLevel1Index(level1, this.props.displayChangeCustomerLink), Math.max(0, level1.length - 1));
            if (next !== this.state.selectedLevel1Index) {
                this.setState({ selectedLevel1Index: next });
            }
        }
    }

    getLevel1Children(): MappedLink[] {
        const { link, displayChangeCustomerLink } = this.props;
        if (!link.children) {
            return [];
        }
        return link.children.filter(child => filterMegaMenuLevel1Child(child, displayChangeCustomerLink));
    }

    closePopover = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    closePopoverOnScroll = () => {
        this.closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    renderMegaMenu(link: MappedLink, index: number, menuLink: React.ReactElement, styles: MainNavigationStyles) {
        const { displayChangeCustomerLink } = this.props;
        const level1Children = this.getLevel1Children();
        const { selectedLevel1Index } = this.state;
        const safeIndex = Math.min(selectedLevel1Index, Math.max(0, level1Children.length - 1));
        const selectedL1 = level1Children[safeIndex];
        const level2Children =
            selectedL1?.children?.filter(gc => filterMegaMenuLevel2Child(gc, displayChangeCustomerLink)) ?? [];

        const megaShell = css`
            display: flex;
            flex-direction: row;
            align-items: stretch;
            width: 100%;
            min-height: 120px;
            max-height: calc(100vh - 100px);
            background: ${getColor("common.background")};
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        `;

        const columnCss = css`
            flex: 1 1 50%;
            min-width: 0;
            display: flex;
            flex-direction: column;
            max-height: calc(100vh - 225px);
            border-left: 1px solid ${getColor("common.global.gray03")};
        `;

        const columnHeaderCss = css`
            flex-shrink: 0;
            font-weight: 800;
            font-size: 16px;
            padding: 16px 16px 12px;
            color: ${getColor("text.main")};
            border-bottom: 1px solid ${getColor("common.global.gray03")};
        `;
        

        const level1ScrollListCss = css`
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
            overflow-x: hidden;
            list-style: none;
            margin: 0;
            padding: 0;
        `;

        const level1RowCss = (isActive: boolean) => css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            min-height: 45px;
            padding: 0 12px 0 16px;
            border-bottom: 1px solid ${getColor("common.global.gray03")};
            border-right: 1px solid ${getColor("common.global.gray03")};
            background: ${isActive ? getColor("common.global.black") : getColor("common.background")};
            cursor: pointer;

            ${!isActive &&
            css`
                &:hover {
                    background: ${getColor("common.global.gray04")} !important;
                }
                &:hover .mega-menu-level1-label {
                    color: ${getColor("primary.main")} !important;
                    text-decoration: underline !important;
                    background: transparent !important;
                }
                &:hover .mega-menu-level1-chevron {
                    color: ${getColor("primary.main")};
                }
            `}

            ${isActive &&
            css`
                &:hover .mega-menu-level1-label {
                    color: #fff !important;
                }
            `}
        `;

        const level1LabelCss = (isActive: boolean) => css`
            flex: 1;
            min-width: 0;
            display: flex;
            align-items: center;
            padding: 10px 8px 10px 0;
            margin: 0;
            border: none;
            background: transparent;
            cursor: pointer;
            font: inherit;
            text-align: left;
            color: ${isActive ? "#fff" : "#000"} !important;
            font-weight: ${isActive ? 700 : 400};
            text-decoration: none;
            &:focus-visible {
                outline: 2px solid ${getColor("primary.main")};
                outline-offset: 2px;
            }
        `;

        const chevronCss = (isActive: boolean) => css`
            flex-shrink: 0;
            color: ${isActive ? "#fff" : getColor("common.border")};
            margin-right: 0 !important;
        `;

        const level2ListCss = css`
            list-style: none;
            margin: 0;
            padding: 0;
        `;

        const level2TitleRowCss = css`
        border-bottom: 1px solid ${getColor("common.global.gray03")};
        border-right: 1px solid ${getColor("common.global.gray03")};
        min-height: 44px;
        display: flex;
        align-items: center;
        padding: 0 16px;
    `;

        const level2RowCss = css`
            border-bottom: 1px solid ${getColor("common.global.gray03")};
            border-right: 1px solid ${getColor("common.global.gray03")};
            min-height: 44px;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: 0 12px 0 16px;
            gap: 8px;
            &:hover {
                background: ${getColor("common.global.gray04")} !important;
            }
            &:hover .mega-menu-level2-chevron {
                color: ${getColor("primary.main")};
            }
        `;

        const allLinkCss = css`
            color: #000 !important;
            background: transparent;
            font-weight: 400;
            text-decoration: none;
            display: block;
            width: 100%;
            padding: 15px 0;

            span {
                color: ${getColor("common.global.black")} !important;
            }

            &:hover span {
                color: ${getColor("primary.main")} !important;
                text-decoration: underline;
                font-weight: 700;
            }
        `;

        const level2LinkCss = css`
            flex: 1 1 auto;
            min-width: 0;
            color: #000 !important;
            background: transparent;
            text-decoration: none;
            font-weight: 400;
            display: block;
            padding: 10px 0;

            span {
                color: ${getColor("common.global.black")} !important;
            }

            &:hover span {
                color: ${getColor("primary.main")} !important;
                text-decoration: underline;
                font-weight: 500;
            }
        `;

        return (
            <Popover
                className="mega-menu"
                zIndexKey="menu"
                {...styles.megaMenu}
                contentBodyProps={{
                    ...styles.megaMenu?.contentBodyProps,
                    _width: 1140,
                    _height: "auto",
                    as: "div",
                    css: css`
                        ${styles.megaMenu?.contentBodyProps?.css}
                        padding: 0;
                    `,
                }}
                controlsId={`${index}_${link.title}`}
                ref={this.popover}
                positionFunction={(element: React.RefObject<HTMLUListElement | null>) => {
                    if (this.props.container.current?.offsetParent) {
                        const {
                            breakpoints: { maxWidths },
                        } = this.props.theme;
                        const parentPosition = this.props.container.current.getBoundingClientRect();
                        const rect = element.current?.getBoundingClientRect();
                        const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) : 0;
                        const cartWidth = 150;
                        const breakpointMaxWidth = maxWidths.find((value: number, i: number) => {
                            return (
                                parentPosition.width + cartWidth <= value &&
                                parentPosition.width + cartWidth > maxWidths[i - 1]
                            );
                        });

                        const width = breakpointMaxWidth ? breakpointMaxWidth - 30 : parentPosition.width;

                        return {
                            top,
                            position: "fixed",
                            left: parentPosition.left,
                            width: "40%", // `${width}px`,
                        };
                    }
                    return { position: "absolute" };
                }}
                popoverTrigger={menuLink as React.ReactElement<any>}
                insideRefs={[this.element]}
            >
                <StyledWrapper className="mega-menu-two-column" css={megaShell} data-test-selector={`mainNavigationItem_${index}`}>
                    <StyledWrapper css={columnCss}>
                        <StyledWrapper css={columnHeaderCss} data-test-selector="megaMenu_level1_heading">
                            {link.title}
                        </StyledWrapper>
                        <StyledWrapper as="ul" css={level1ScrollListCss} data-test-selector="megaMenu_level1_list">
                            {level1Children.map((child, childIndex) => {
                                const showSubMenu = hasSubcategoryMenu(child, displayChangeCustomerLink);
                                const isActive = childIndex === safeIndex;
                                return (
                                    <StyledWrapper
                                        as="li"
                                        key={`${child.title}_${childIndex}`}
                                        css={level1RowCss(isActive)}
                                        data-selected={isActive}
                                        data-test-selector={`megaMenu_level1_row_${childIndex}`}
                                    >
                                        <StyledWrapper
                                            as="button"
                                            type="button"
                                            className="mega-menu-level1-label"
                                            css={level1LabelCss(isActive)}
                                            aria-current={isActive ? "true" : undefined}
                                            onClick={() => this.setState({ selectedLevel1Index: childIndex })}
                                            data-test-selector={child.title}
                                        >
                                            {child.title}
                                        </StyledWrapper>
                                        {showSubMenu && (
                                            <StyledIcon
                                                className="mega-menu-level1-chevron"
                                                src={ChevronRight}
                                                size={24}
                                                css={chevronCss(isActive)}
                                            />
                                        )}
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper css={columnCss}>
                        <StyledWrapper
                            css={css`
                                flex: 1 1 auto;
                                min-height: 0;
                                overflow-y: auto;
                                overflow-x: hidden;
                                background: ${getColor("common.background")};
                            `}
                            data-test-selector="megaMenu_level2_panel"
                        >
                            {selectedL1 && (
                                <>
                                    <StyledWrapper css={level2TitleRowCss}>
                                        <Link
                                            className="mega-menu-all-link"
                                            css={allLinkCss}
                                            href={selectedL1.url}
                                            onClick={this.closePopover}
                                            data-test-selector={`all_${selectedL1.title}`}
                                        >
                                            {translate("All")} {selectedL1.title}
                                        </Link>
                                    </StyledWrapper>
                                    {level2Children.length > 0 && (
                                        <StyledWrapper as="ul" css={level2ListCss} data-test-selector={`linksFor_${selectedL1.title}`}>
                                            {level2Children.map((grandChild, gcIndex) => (
                                                <StyledWrapper as="li" key={gcIndex} css={level2RowCss}>
                                                    <Link
                                                        className="mega-menu-link"
                                                        css={level2LinkCss}
                                                        onClick={this.closePopover}
                                                        href={grandChild.url}
                                                        data-test-selector={grandChild.title}
                                                    >
                                                        {grandChild.title}
                                                    </Link>
                                                </StyledWrapper>
                                            ))}
                                        </StyledWrapper>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Popover>
        );
    }

    render() {
        const { styles, link, index, isOpen } = this.props;
        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading =
            hasChildren && (link.childrenType === "CascadingMenu" || link.childrenType === "CustomLinkList");
        const isMega = hasChildren && link.childrenType === "MegaMenu";

        let menuLink = (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
            >
                {link.title}
                {isCascading && downIcon}
            </Link>
        );
        if (isMega || link.url === "") {
            menuLink = (
                <Link
                    className="menu-item"
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                >
                    {link.title}
                    {(isCascading || isMega) && downIcon}
                </Link>
            );
        }

        let menuItem = menuLink;

        if (isCascading && link.children) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }
            menuItem = (
                <Menu
                    className="cascading-menu"
                    data-test-selector={`mainNavigationItem_${index}`}
                    isOpen={isOpen || undefined}
                    descriptionId={`${link.title}_${index}`}
                    menuItems={link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                />
            );
        }

        if (isMega) {
            menuItem = this.renderMegaMenu(link, index, menuLink, styles);
        }

        return menuItem;
    }
}

export default withTheme(MainNavigationItem);
